import { useSyncExternalStore } from 'react';

/**
 * PWA: registro del service worker (`public/sw.js`) y aviso de versión nueva.
 *
 * Cada despliegue publica un service worker nuevo (con su versión). Cuando termina de instalarse
 * queda «esperando»: `updateReady` pasa a true y `UpdateNotice` avisa. Al aplicar la actualización
 * (SKIP_WAITING) el service worker nuevo toma el control y la página se recarga una vez.
 *
 * No se registra en desarrollo ni dentro de la app Android (Capacitor sirve los archivos locales).
 */
const CHECK_EVERY_MS = 30 * 60_000;

let waiting: ServiceWorker | null = null;
let updateReady = false;
/** Solo se recarga al cambiar de service worker si fue una actualización pedida (no en la 1.ª visita). */
let updateRequested = false;
const listeners = new Set<() => void>();
const setReady = (w: ServiceWorker | null) => {
  waiting = w;
  updateReady = !!w;
  listeners.forEach((l) => l());
  if (TEST) (globalThis as unknown as { __sportsarenaPwa?: unknown }).__sportsarenaPwa = { updateReady };
};
const TEST = (() => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('sportsarena.debug') === '1';
  } catch {
    return false;
  }
})();

/** ¿Dentro de la app nativa (Capacitor)? */
export const isNativeApp = (): boolean =>
  typeof window !== 'undefined' &&
  !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

export const registerServiceWorker = (): void => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !import.meta.env.PROD || isNativeApp() || import.meta.env.VITE_PWA === '0') return;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !updateRequested) return;
    reloading = true;
    window.location.reload();
  });
  const start = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      const watch = (w: ServiceWorker | null) =>
        w?.addEventListener('statechange', () => {
          // Instalado con otro ya activo: es una actualización (la primera instalación no avisa).
          if (w.state === 'installed' && navigator.serviceWorker.controller) setReady(reg.waiting ?? w);
        });
      if (reg.waiting && navigator.serviceWorker.controller) setReady(reg.waiting);
      reg.addEventListener('updatefound', () => watch(reg.installing));
      const check = () => void reg.update().catch(() => {});
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      if (TEST) (globalThis as unknown as { __sportsarenaPwaCheck?: () => void }).__sportsarenaPwaCheck = check;
    } catch (err) {
      console.warn('[pwa] No se pudo registrar el service worker:', err);
    }
  };
  if (document.readyState === 'complete') void start();
  else window.addEventListener('load', () => void start(), { once: true });
};

/** Activa la versión nueva (la página se recarga sola al tomar el control). */
export const applyUpdate = (): void => {
  if (!waiting) return;
  updateRequested = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
};

export const useUpdateReady = (): boolean =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => updateReady,
    () => false
  );
